import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import { getArtistSharedTicketSales, type ArtistSharedTicketSales } from '../../../api/taskEvents';
import { ApiStatusError } from '../../../api/templates';
import '../../../css/artist/events/ArtistSharedTicketSales.css';

const currentJstMonth = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value || '';
  const month = parts.find((part) => part.type === 'month')?.value || '';
  return `${year}-${month}`;
};

const shiftMonth = (month: string, delta: number) => {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, monthNumber - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const formatMonth = (month: string) => {
  const [year, monthNumber] = month.split('-');
  return `${year}年${Number(monthNumber)}月`;
};

const formatCount = (value: number | null) => value == null ? '—' : `${value.toLocaleString('ja-JP')}枚`;
const formatPercent = (value: number | null) => value == null ? '—' : `${value.toLocaleString('ja-JP')}%`;

export default function ArtistSharedTicketSalesPanel({ eventUuid }: { eventUuid: string }) {
  const currentMonth = useMemo(currentJstMonth, []);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState<ArtistSharedTicketSales | null>(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getArtistSharedTicketSales(eventUuid, month)
      .then((next) => {
        if (!active) return;
        setData(next);
        setVisible(true);
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ApiStatusError && error.status === 404) {
          setVisible(false);
          setData(null);
          return;
        }
        // 背景読み取りの一過性失敗は既存表示を維持し、操作を妨げない。
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [eventUuid, month]);

  if (!visible) return null;

  const summary = data?.summary;
  const progressWidth = Math.max(0, Math.min(100, summary?.progressPercent || 0));

  return (
    <section className="event-panel artist-shared-ticket-sales" id="ticket-sales-shared">
      <div className="panel-heading">
        <div>
          <h3>券売状況</h3>
          <p>主催者から共有された販売枚数です。金額情報は含まれません。</p>
        </div>
      </div>

      {loading && !data ? (
        <p className="artist-shared-ticket-sales__loading" role="status">券売状況を読み込んでいます。</p>
      ) : data && summary ? (
        <>
          <dl className="artist-shared-ticket-sales__summary">
            <div><dt>販売枚数</dt><dd>{formatCount(summary.sold)}</dd></div>
            <div><dt>残り枚数</dt><dd>{formatCount(summary.remaining)}</dd></div>
            <div><dt>販売率</dt><dd>{formatPercent(summary.progressPercent)}</dd></div>
          </dl>
          {summary.capacity != null ? (
            <div className="artist-shared-ticket-sales__progress">
              <div
                role="progressbar"
                aria-label="チケット販売率"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.min(100, summary.progressPercent || 0)}
              >
                <span style={{ width: `${progressWidth}%` }} />
              </div>
              <small>上限 {formatCount(summary.capacity)}</small>
            </div>
          ) : null}

          <div className="artist-shared-ticket-sales__types">
            <h4>チケット種別</h4>
            <div className="artist-shared-ticket-sales__table-wrap">
              <table>
                <thead><tr><th>種別</th><th>販売</th><th>残り</th><th>販売率</th></tr></thead>
                <tbody>
                  {data.ticketTypes.map((ticket) => (
                    <tr key={ticket.ticketTypeId}>
                      <th scope="row">{ticket.name}</th>
                      <td data-label="販売">{formatCount(ticket.sold)}</td>
                      <td data-label="残り">{formatCount(ticket.remaining)}</td>
                      <td data-label="販売率">{formatPercent(ticket.progressPercent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="artist-shared-ticket-sales__daily">
            <div className="artist-shared-ticket-sales__month-nav">
              <DsButton variant="secondary" size="sm" onClick={() => setMonth(shiftMonth(month, -1))}>
                前の月
              </DsButton>
              <h4>{formatMonth(month)}の日別販売</h4>
              <DsButton
                variant="secondary"
                size="sm"
                onClick={() => setMonth(shiftMonth(month, 1))}
                disabled={month >= currentMonth}
              >
                次の月
              </DsButton>
            </div>
            {data.days.length ? (
              <ul>
                {data.days.map((day) => (
                  <li key={day.date}><time dateTime={day.date}>{Number(day.date.slice(5, 7))}月{Number(day.date.slice(8, 10))}日</time><strong>{formatCount(day.sold)}</strong></li>
                ))}
              </ul>
            ) : <p className="artist-shared-ticket-sales__empty">この月の販売記録はありません。</p>}
          </div>
        </>
      ) : null}
    </section>
  );
}
